import {ReactNode} from "react";
import ImagePackElement from "./imagePack.element";
import {useApp} from "../app.context";
import Checkmark from "./checkmark";
import {useNavTarget} from "./useNavTarget";
import {NavTarget} from "../utils/navTarget";

interface EntityLabelProps {
    id: string;
    onDelete?(): void;
    // Appended to the localized name. Used by the Tech Tree tab to show which
    // tech node has unlocked this entity, e.g. " (Exploration)".
    suffix?: string;
    // Image-pack level to render — Buildings need startLevel because their
    // pack reserves index 0 as a dead slot. Units / Resources / Tiles use
    // level 0 (the default).
    level?: number;
    // Ctrl/Cmd-click navigation target (the kind can't be inferred from id
    // alone, so the call site supplies it). Omitted → not navigable.
    navTarget?: NavTarget;
    // Frees the entity from whatever currently holds it. Omitted → no button.
    // Used by the Tech Tree palette, where the only other way to unassign is to
    // find the node holding the unlock and drag it off.
    onUnassign?(): void;
    // Tooltip on that button, e.g. "Unassign from Writing". Defaults to a bare
    // "Unassign".
    unassignTitle?: string;
    // A second line under the name — the Tech Tree palette shows the buildings
    // that produce a unit there. Wrap it in a .row-links element to keep it at
    // full strength in a .dim-row-content row.
    detail?: ReactNode;
}

function EntityLabel(props: EntityLabelProps) {
    const {getLocalizationValue, confirmDelete} = useApp();

    return (
        <div className={'layout horizontal stretch gap label'} {...useNavTarget(props.navTarget)} style={{alignItems: 'center', padding: 5}}>
            <div className={'layout horizontal gap dim-through'} style={{alignItems: 'center'}}>
                <div style={{position: 'relative'}}>
                    <ImagePackElement id={props.id} level={props.level ?? 0} size={50}/>
                    <Checkmark id={props.id} style={{position: 'absolute', top: 2, right: 2}}/>
                </div>
                {props.detail
                    ? <div className={'layout vertical dim-through'} style={{padding: '0 5px', gap: 3}}>
                        <p style={{width: 160}}>{getLocalizationValue(props.id)}</p>
                        {props.detail}
                    </div>
                    : <p style={{width: 160, padding: 5}}>{getLocalizationValue(props.id)}</p>}
            </div>
            {props.suffix && <p style={{marginLeft: 'auto', padding: 5}}>{props.suffix.trim()}</p>}
            {props.onUnassign && <button className={'row-action'}
                                         style={{marginLeft: props.suffix ? undefined : 'auto'}}
                                         title={props.unassignTitle ?? 'Unassign'}
                                         onClick={(e) => {
                                             // The row opens the description popup on click and is a
                                             // Ctrl/Cmd-click navigation target; neither should fire.
                                             e.stopPropagation();
                                             props.onUnassign?.();
                                         }}>
                <span className={'fa-solid fa-link-slash'}/>
            </button>}
            {props.onDelete && <button style={{marginRight: 8}}>
                <span className={'fa-solid fa-trash'} style={{color: 'white'}} onClick={async (e) => {
                    e.stopPropagation();
                    if (props.onDelete && await confirmDelete(getLocalizationValue(props.id))) {
                        props.onDelete();
                    }
                }}/>
            </button>}
        </div>
    )
}

export default EntityLabel;